import axios from 'axios'
import store from '@/store'
import storage from 'store'
import notification from 'ant-design-vue/es/notification'
import message from 'ant-design-vue/es/message'
import { VueAxios } from './axios'
import {ACCESS_TOKEN, APP_LANGUAGE, SENSITIVE_HIDDEN, SENSITIVE_MODE, SENSITIVE_LOCKED} from '@/store/mutation-types'
import { tansParams, blobValidate } from "@/utils/ruoyi";
import {
  maskData,
  findMaskedSensitiveField,
  SENSITIVE_CONFIG_KEY_HIDDEN,
  SENSITIVE_CONFIG_KEY_MODE
} from '@/utils/sensitive'
import cache from '@/plugins/cache'
import errorCode from '@/utils/errorCode'
import qs from 'qs'
import { saveAs } from 'file-saver'

// 是否显示重新登录
let isReloginShow
function handleLoginExpired () {
  if (isReloginShow || !storage.get(ACCESS_TOKEN)) return
  isReloginShow = true
  notification.error({ message: '登录状态已过期，请重新登录' })
  store.dispatch('ClearLogin').finally(() => {
    location.href = '/index'
  })
}
axios.defaults.headers['Content-Type'] = 'application/json;charset=utf-8'
// 创建 axios 实例
const request = axios.create({
  // API 请求的默认前缀
  baseURL: process.env.VUE_APP_BASE_API,
  timeout: 100000 // 请求超时时间
})

// 异常拦截处理器
const errorHandler = (error) => {
  if (error.response && error.response.status === 401) {
    handleLoginExpired()
    return Promise.reject(error)
  }
  console.log('err' + error)
  let { message } = error
  if (message === 'Network Error') {
    message = '后端接口连接异常'
  } else if (message.includes('timeout')) {
    message = '系统接口请求超时'
  } else if (message.includes('Request failed with status code')) {
    message = '系统接口' + message.substr(message.length - 3) + '异常'
  }
  notification.error({
    message: message,
    duration: 5
  })
  return Promise.reject(error)
}

// ============ 敏感信息脱敏：读取服务端参数配置（sys_config）============
/** 是否为参数配置查询接口（避免递归加载、且静默处理异常） */
function isSensitiveConfigUrl (url) {
  return !!url && url.indexOf('/system/config/configKey/') > -1
}

/** 把服务端参数配置的值应用到本地开关（参数配置优先级高于本地设置） */
function applySensitiveConfig (value, type) {
  if (value === null || value === undefined || value === '') {
    return
  }
  const val = String(value).trim().toLowerCase()
  if (type === SENSITIVE_HIDDEN) {
    if (val === 'true') {
      // 服务端开启：强制脱敏并锁定前端本地开关，避免被随意关闭
      store.commit(SENSITIVE_HIDDEN, true)
      store.commit(SENSITIVE_LOCKED, true)
    } else {
      // 服务端关闭：仅解锁，保留用户本地开关状态
      store.commit(SENSITIVE_LOCKED, false)
    }
  } else if (type === SENSITIVE_MODE) {
    store.commit(SENSITIVE_MODE, val === 'hide' ? 'hide' : 'mask')
  }
}

/** 每个页面周期只加载一次敏感信息脱敏配置 */
let sensitiveConfigPromise = null
function loadSensitiveConfig () {
  if (sensitiveConfigPromise) {
    return sensitiveConfigPromise
  }
  const query = key => request({
    url: '/system/config/configKey/' + key,
    method: 'get'
  }).then(res => {
    applySensitiveConfig(res ? res.data : null, key === SENSITIVE_CONFIG_KEY_HIDDEN ? SENSITIVE_HIDDEN : SENSITIVE_MODE)
  }).catch(() => {
    // 参数未配置或接口异常时忽略，沿用本地开关
  })
  sensitiveConfigPromise = Promise.all([
    query(SENSITIVE_CONFIG_KEY_HIDDEN),
    query(SENSITIVE_CONFIG_KEY_MODE)
  ])
  return sensitiveConfigPromise
}
// ============ 敏感信息脱敏配置 end ============

// request interceptor
request.interceptors.request.use(async config => {
  // 首个业务请求前先加载参数配置中的脱敏开关，保证首屏数据也被正确脱敏
  if (!isSensitiveConfigUrl(config.url)) {
    await loadSensitiveConfig()
  }
  const token = storage.get(ACCESS_TOKEN)
  const lang = storage.get(APP_LANGUAGE)

  // 是否需要防止数据重复提交
  const isRepeatSubmit = (config.headers || {}).repeatSubmit === false
  // 如果 token 存在
  // 让每个请求携带自定义 token 请根据实际情况自行修改
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token // 让每个请求携带自定义token 请根据实际情况自行修改
    config.headers['Accept-Language'] = lang // 让每个请求携带语言
    // config.headers['accessAccess-Token'] = token
  }
  // 处理params参数
  if (config.params) {
    const url = config.url + '?' + qs.stringify(config.params, { indices: false })
    config.params = {}
    config.url = url
  }
  // get请求映射params参数
  if (config.method === 'get' && config.params) {
    let url = config.url + '?' + tansParams(config.params);
    url = url.slice(0, -1);
    config.params = {};
    config.url = url;
  }
  if (!isRepeatSubmit && (config.method === 'post' || config.method === 'put' || config.method === 'delete')) {
    const requestObj = {
      url: config.url,
      data: typeof config.data === 'object' ? JSON.stringify(config.data) : config.data,
      time: new Date().getTime()
    }
    const sessionObj = cache.session.getJSON('sessionObj')
    if (sessionObj === undefined || sessionObj === null || sessionObj === '') {
      cache.session.setJSON('sessionObj', requestObj)
    } else {
      const s_url = sessionObj.url;                  // 请求地址
      const s_data = sessionObj.data;                // 请求数据
      const s_time = sessionObj.time;                // 请求时间
      const interval = 1000;                         // 间隔时间(ms)，小于此时间视为重复提交
      if (s_data === requestObj.data && requestObj.time - s_time < interval && s_url === requestObj.url) {
        const message = '数据正在处理，请勿重复提交';
        console.warn(`[${s_url}]: ` + message)
        return Promise.reject(new Error(message))
      } else {
        cache.session.setJSON('sessionObj', requestObj)
      }
      cache.session.setJSON('sessionObj', requestObj)
    }
  }
  // 脱敏模式开启时，禁止把脱敏后的数据提交回后端，避免污染真实数据
  if (config.method === 'post' || config.method === 'put') {
    const maskedField = findMaskedSensitiveField(config.data, 0)
    if (maskedField) {
      message.error(`敏感信息脱敏已开启，字段【${maskedField}】为脱敏数据，已阻止提交，请关闭脱敏后重新编辑`)
      return Promise.reject(new Error('masked sensitive data: ' + maskedField))
    }
  }
  return config
}, errorHandler)

// response interceptor
request.interceptors.response.use((res) => {
  //console.log('响应数据', res)
  // 请求rul
  const requestUrl = res.config.url
  // 未设置状态码则默认成功状态
  const code = res.data.code || '20001'
  // 获取错误信息
  const msg = errorCode[code] || res.data.message || res.data.msg|| errorCode['default']
  // 脱敏开关的参数配置查询失败（未配置/异常）时静默处理，不打断用户操作
  const silent = isSensitiveConfigUrl(requestUrl)
  // 二进制数据则直接返回
  if (res.request.responseType === 'blob' || res.request.responseType === 'arraybuffer') {
    return res.data
  }
  if (['401', '20231', '20230'].includes(String(code))) {
    handleLoginExpired()
    return Promise.reject(new Error('登录状态已过期，请重新登录'))
  } else if (!silent && code === 500) {
    if (requestUrl !== '/login') {
      notification.error({
        message: msg,
        description: msg
      })
    }
  } else if (!silent && code === 601) {
    notification.error({
      message: msg
    })
  } else if (!silent && (code === "40000" || code === "20200" || code === "20210")) {
    notification.error({
      message: msg
    })
  } else if (code !== '20001' && code !== 1) {
    notification.error({ message: msg })
    return Promise.reject(new Error('error'))
  } else {
    // 敏感信息全局脱敏：手机号 / 身份证 / 银行卡 / 营业执照等（开关关闭时不做任何处理）
    maskData(res.data, 0)
    return res.data
  }
}, errorHandler)

const installer = {
  vm: {},
  install(Vue) {
    Vue.use(VueAxios, request)
  }
}

// 通用下载方法
export function download(url, params, filename) {
  const notificationKey = 'download'
  notification.open({
    key: notificationKey,
    message: '正在下载数据，请稍候',
    duration: null,
    icon: h => {
      return h(
        'a-icon',
        {
          props: {
            type: 'loading'
          }
        }
      )
    }
  })
  return request({
    url: url,
    method: 'post',
    data: params,
    //transformRequest: (params) => { return qs.stringify(params, { indices: false }) },
    headers: { 'Content-Type': 'application/json' },
    responseType: 'blob'
  }).then(async (data) => {
    const isLogin = await blobValidate(data)
    if (isLogin) {
      const blob = new Blob([data])
      saveAs(blob, filename)
      message.success('下载成功')
    } else {
      const resText = await data.text()
      const rspObj = JSON.parse(resText)
      const errMsg = errorCode[rspObj.code] || rspObj.msg || errorCode['default']
      message.error(errMsg)
    }
    notification.close(notificationKey)
  }).catch((r) => {
    message.error('下载文件出现错误，请联系管理员！')
    notification.close(notificationKey)
  })
}

export default request

export {
  installer as VueAxios,
  request as axios
}
